import { freeTrialStatus } from '$src/api/constants';
import { controlCenterApi } from '$src/api/control-center-api';
import { Notify } from '$src/utils/notify';
import { action, computed, makeObservable, observable } from 'mobx';

export default class LocationContextStore {
  location = null;

  constructor(rootStore) {
    makeObservable(this, {
      location: observable,
      fetchLocation: action,
      isFreeTrialActive: computed,
      isFreeTrialUnavailable: computed,
      isFreeTrialAvailable: computed,
      hasCustomSettings: computed,
      locationOwnerId: computed,
    });
    this.rootStore = rootStore;
  }

  fetchLocation = async (userId, locationId) => {
    if (this.location?.locationId === locationId) return;

    try {
      const { locationStore } = this.rootStore;

      /**
       * Ensures locations are loaded and finds the requested location.
       * Locations must be fetched first as they contain permission data
       * and owner information required for subsequent operations.
       */
      if (!locationStore.locations) {
        try {
          await locationStore.fetchLocations(userId);
        } catch (error) {
          throw new Error(`Failed to fetch locations: ${error.message}`);
        }
      }

      if (!locationStore.locations?.length) {
        throw new Error('No locations available for the current user');
      }

      const location = locationStore.locations.find((l) => l.locationId === locationId);

      if (!location) {
        throw new Error(`Location with ID ${locationId} not found in user's allowed locations`);
      }

      const subscription = await controlCenterApi.fetchSubscription(location.ownerId, locationId);

      this.setLocation({
        locationId,
        ownerId: location.ownerId,
        isOwner: location.ownerId === userId,
        freeTrialStatus: subscription?.freeTrialStatus,
        overagesCap: subscription?.overageCap,
        hasEntitlements: subscription?.entitlements?.monitoring ?? false,
        freeTrialStartedTimestamp: subscription?.freeTrialStartedTimestamp,
        freeTrialExpirationTimestamp: subscription?.freeTrialExpirationTimestamp,
        overagesThreshold: subscription?.entitlements?.overages_threshold,
        overagesStartAt: subscription?.entitlements?.overages_start_at,
        overagesEnabled: subscription?.entitlements?.overages_enabled,
      });
    } catch (e) {
      this.setLocation({ locationId, hasEntitlements: false });
      Notify.error('locationContextStore: fetchLocation failure', e);
    }
  };

  setLocation(location) {
    this.location = location;
  }

  get locationOwnerId() {
    return this.location?.ownerId;
  }

  get isFreeTrialAvailable() {
    return this.location?.freeTrialStatus === freeTrialStatus.AVAILABLE;
  }

  get isFreeTrialActive() {
    return this.location?.freeTrialStatus === freeTrialStatus.ACTIVE;
  }

  get isFreeTrialUnavailable() {
    return this.location?.freeTrialStatus === freeTrialStatus.UNAVAILABLE;
  }

  get hasCustomSettings() {
    const customSetting = {
      devices: this.rootStore.devicesStore.subscribedDevices.length > 0,
      preferences: this.rootStore.preferencesStore.preferences.default !== undefined ? !this.rootStore.preferencesStore.preferences.default : false,
      emergencyContacts: true,
    };

    return customSetting.devices || customSetting.preferences || customSetting.emergencyContacts ? customSetting : false;
  }

  cancelFreeTrial = async (userId, callback) => {
    try {
      await controlCenterApi.cancelFreeTrial(userId, this.location.locationId);
      if (callback) callback();
    } catch (e) {
      Notify.error('There was an error canceling the free trial', e);
    }
  };

  subscribeToFreeTrial = async (userId, locationId) => {
    await controlCenterApi.createEntitlement(userId, locationId);
  };

  completeOnboarding = async (userId, locationId) => {
    await controlCenterApi.completeOnboarding(userId, locationId);
  };

  setOnboardingCompleted = async (userId, locationId) => {
    await controlCenterApi.setOnboardingCompleted(userId, locationId);
  };
}
